'use client';

import Image from '@/components/Image';
import { useTranslations } from '@/i18n/use-translations';
import Link from 'next/link';
import { useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { usePersonalityFitTracking } from '../hooks/usePersonalityFitTracking';

interface BannerProps {
  bannerData: any;
  bannerIndex?: number;
  className?: string;
  sizes?: string; // @/components/Image sizes
}

export default function Banner({
  bannerData,
  bannerIndex = 0,
  className,
  sizes = '100vw',
}: BannerProps) {
  const t = useTranslations('activity.personalityFitGlasses.banner');
  const { trackMktClick, trackMktExp } = usePersonalityFitTracking();
  const [expRef, inView] = useInView({ threshold: 0.6, triggerOnce: true });

  const img = bannerData?.image || bannerData?.img || bannerData?.url || '';
  const jumpType = Number(bannerData?.jump_type || 0);
  const jumpValue = String(bannerData?.jump_value || '');
  const hasAnimation = Number(bannerData?.has_animation || 0) === 1;
  const aspectRatio = bannerData?.aspectRatio as string | undefined;

  // 构建埋点参数
  const buildTrackParams = useCallback(() => {
    const params: any = { mkt_name: `Banner_${bannerIndex}` };
    if (jumpType === 2) {
      // 跳转详情页：传递 shop_sku, business_sku, product_sku
      params.shop_sku = bannerData?.shop_sku || '';
      params.business_sku = bannerData?.business_sku || '';
      params.product_sku = jumpValue;
    } else if (jumpType === 1) {
      // 跳转列表页：传递 group_id
      params.group_id = jumpValue;
    }
    return params;
  }, [bannerIndex, jumpType, jumpValue, bannerData?.shop_sku, bannerData?.business_sku]);

  // 曝光上报
  useEffect(() => {
    if (inView) {
      try {
        trackMktExp(buildTrackParams());
      } catch {
        /* noop */
      }
    }
  }, [inView, buildTrackParams, trackMktExp]);

  let href = '#';
  if (jumpType === 2) {
    href = `/goods-detail/${jumpValue}`;
  } else if (jumpType === 1) {
    href = `/goods-list/${jumpValue}`;
  }

  if (!img) {
    return (
      <div
        className={`w-full ${className || ''}`}
        style={aspectRatio ? { aspectRatio } : undefined}
      />
    );
  }

  // 可滚动动画（第二张 banner 可配置）
  if (hasAnimation) {
    const Animated = (
      <div
        className={`banner-viewport overflow-hidden rounded-[12px] md:rounded-[16px] ${className || ''}`}
        style={aspectRatio ? { aspectRatio } : undefined}
      >
        <div className="banner-track flex w-[200%] h-full">
          <Image
            src={img}
            alt={`${t('imgAlt')}-${bannerIndex}`}
            width={1440}
            height={520}
            className="scrolling-image w-1/2 h-full object-cover"
            sizes={sizes}
            priority={bannerIndex === 0}
          />
          <Image
            src={img}
            alt={`${t('imgAlt')}-${bannerIndex}`}
            width={1440}
            height={520}
            className="scrolling-image w-1/2 h-full object-cover"
            sizes={sizes}
            priority={bannerIndex === 0}
          />
        </div>
        <style>{`
          .scrolling-image { animation: pf-scroll-image 25s linear infinite; }
          @keyframes pf-scroll-image { 0% { transform: translateX(-100%); } 100% { transform: translateX(0); } }
        `}</style>
      </div>
    );

    return href === '#' ? (
      <div ref={expRef} className="w-full">
        {Animated}
      </div>
    ) : (
      <Link
        href={href}
        className="block w-full"
        onClick={() => {
          // 列表页跳转需要标记 sessionStorage
          if (jumpType === 1 && typeof window !== 'undefined' && window.sessionStorage) {
            try {
              window.sessionStorage.setItem('zl_goodsListEnterPageScene', '活动专题页');
            } catch {
              /* noop */
            }
          }
          try {
            trackMktClick(buildTrackParams());
          } catch {
            /* noop */
          }
        }}
      >
        <div ref={expRef} className="w-full">
          {Animated}
        </div>
      </Link>
    );
  }

  const content = (
    <div
      className={`w-full overflow-hidden rounded-[12px] md:rounded-[16px] ${className || ''}`}
      style={aspectRatio ? { aspectRatio } : undefined}
    >
      <Image
        src={img}
        alt={`${t('imgAlt')}-${bannerIndex}`}
        width={1440}
        height={520}
        className="w-full h-auto"
        sizes={sizes}
        priority={bannerIndex === 0}
      />
    </div>
  );

  if (href === '#')
    return (
      <div ref={expRef} className="w-full">
        {content}
      </div>
    );

  return (
    <Link
      href={href}
      className="block w-full"
      onClick={() => {
        // 列表页跳转需要标记 sessionStorage
        if (jumpType === 1 && typeof window !== 'undefined' && window.sessionStorage) {
          try {
            window.sessionStorage.setItem('zl_goodsListEnterPageScene', '活动专题页');
          } catch {
            /* noop */
          }
        }
        try {
          trackMktClick(buildTrackParams());
        } catch {
          /* noop */
        }
      }}
    >
      <div ref={expRef} className="w-full">
        {content}
      </div>
    </Link>
  );
}
